import {
  Box,
  Button,
  Card,
  Checkbox,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import EditIcon from "assets/icons1/EditIcon.svg";
import { CustomInput } from "global/components";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { fetchProfileDetails, updateProfileDetails } from "../SettingsService";
import { useEffect, useState } from "react";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import {
  getProfileFormData,
  validateForProfileForm,
} from "../General/GeneralTypesAndValidation";
import notifiers from "global/constants/NotificationConstants";
import { updateUserName, updateEmail, selectId } from "redux/authSlice";
import { useAppDispatch, useAppSelector } from "utils/hooks";

const MyProfile = () => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectId);
  const [loader, setLoader] = useState<boolean>(false);
  const [profile, setProfile] = useState<any>(getProfileFormData());
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoader(true);
      const response = await fetchProfileDetails();
      setProfile(getProfileFormData(response));
      setLoader(false);
    } catch (error: any) {
      setLoader(false);
      handleErrorMessage(error);
    }
  };

  const handleOnChangeInputField = (event: React.ChangeEvent<any>) => {
    const newProfile = {
      ...profile,
      [event.target.name]: {
        value: event.target.value,
        error: "",
      },
    };
    setProfile(newProfile);
  };

  const updateProfile = async () => {
    if (!handleValidation()) {
      return;
    }
    try {
      setLoader(true);
      const body = {
        id: userId,
        name: profile.name.value,
        email: profile.email.value,
        contactNo: profile.contactNo.value,
        role: profile.role.value,
        parentEmail: profile.parentEmail,
        roleIds: profile.roleIds.value,
      };
      await updateProfileDetails(body);
      dispatch(updateUserName(body.name));
      dispatch(updateEmail(body.email));
      setLoader(false);
      openSuccessNotification(notifiers.UPDATE);
      setIsEdit(false);
    } catch (error: any) {
      setLoader(false);
      handleErrorMessage(error);
    }
  };

  const handleValidation = () => {
    const { isValid, errors }: { isValid: boolean; errors: any } =
      validateForProfileForm(profile);
    setProfile({ ...errors });
    return isValid;
  };

  const getPageHeader = () => {
    return (
      <>
        <Stack
          sx={{
            width: "100%",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" color="text.primary">
            My Profile
          </Typography>

          <Button
            id="save_btn"
            variant="contained"
            onClick={() => updateProfile()}
            disabled={!isEdit || loader}
          >
            Update Profile
          </Button>
        </Stack>
      </>
    );
  };

  const getEditMyProfile = () => {
    return (
      <Card sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flexShrink: 0 }}>
            Profile Details
          </Typography>
          <IconButton onClick={() => setIsEdit(!isEdit)}>
            <img src={EditIcon} alt="Edit Icon" />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              sx={{
                pb: 0.5,
              }}
            >
              <CustomInput
                label="Email"
                placeHolder="Enter email"
                value={profile?.email.value}
                disabled={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              sx={{
                pb: 0.5,
              }}
            >
              <CustomInput
                id="name"
                label="Name"
                required
                placeHolder="Enter name"
                name="name"
                type="text"
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 2, maxLength: 30 }}
                value={profile.name.value}
                disabled={!isEdit}
                error={profile.name.error}
                helperText={profile.name.error}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              sx={{
                pb: 0.5,
              }}
            >
              <CustomContactNumberInput
                id="contact"
                name="contactNo"
                label="Phone"
                required
                value={profile.contactNo.value}
                placeHolder="Enter phone"
                onChange={(phone: any) => {
                  setProfile({
                    ...profile,
                    contactNo: { value: phone, error: "" },
                  });
                }}
                error={profile.contactNo.error}
                helperText={profile.contactNo.error}
                disabled={!isEdit}
                flagDisabled={!isEdit}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              sx={{
                pb: 0.5,
              }}
            >
              <CustomInput
                id="role"
                label="Role"
                placeHolder="Role"
                name="role"
                type="text"
                value={profile.role.value}
                disabled={true}
              />
            </Box>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const getViewMyProfileBody = () => {
    return (
      <Stack direction="column" spacing={1}>
        {getPageHeader()}
        <Box pt={1}>
          {loader ? (
            <Skeleton animation="wave" variant="rounded" height={200} />
          ) : (
            getEditMyProfile()
          )}
        </Box>
      </Stack>
    );
  };

  const getNewsLetter = () => {
    return (
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={12}>
          <Card sx={{ p: 2.5 }}>
            <Stack direction="column" spacing={1}>
              <Typography variant="h6">Newsletter</Typography>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                  sx={{
                    padding: "0px !important",
                    mr: 0.5,
                  }}
                />
                <Typography display="flex" variant="subtitle2">
                  Send me marketing tips and news about Mailzzy updates.
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const getTermsOFPrivacy = () => {
    return (
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={12}>
          <Stack bgcolor="#FFF9F3" p={2}>
            <Typography variant="body2" color="#FF9436" fontSize="12px">
              Terms of Service: By subscribing to a Mailzzy account or using the
              platform and services offered by Mailzzy you agree on behalf of
              your organization to comply with Mailzzy’s{" "}
              <a
                href="https://mailzzy.com/terms-of-use/"
                target="_blank"
                style={{
                  textDecoration: "none",
                }}
                rel="noreferrer"
              >
                <span>Terms of Use </span>
              </a>
              and{" "}
              <a
                href="https://mailzzy.com/privacy-policy/"
                target="_blank"
                style={{
                  textDecoration: "none",
                }}
                rel="noreferrer"
              >
                <span
                  style={{
                    textDecoration: "none",
                  }}
                >
                  Privacy Policy.
                </span>
              </a>
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    );
  };

  return (
    <CenteredAuthenticatedLayout>
      {getViewMyProfileBody()}
      {loader ? (
        <Box mt={2}>
          <Skeleton animation="wave" variant="rounded" height={100} />
        </Box>
      ) : (
        getNewsLetter()
      )}
      {loader ? (
        <Box mt={2}>
          <Skeleton animation="wave" variant="rounded" height={75} />
        </Box>
      ) : (
        getTermsOFPrivacy()
      )}
    </CenteredAuthenticatedLayout>
  );
};

export default MyProfile;
